import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Products from './Products';
import TagFilter from './TagFilter';

import api from '../../api';

import { selectSectionAndCategory } from '../../actions';
import { getProductSection } from '../../utils/getProductSection';
import { isBuilderProduct } from '../../utils/builderCollections';
import enrichProductsWithBoldOptions from '../../utils/enrichBoldOptions';

// Central mapping of section -> collection handle (kept outside component to avoid reallocation)
const SECTION_HANDLE_MAP = {
    hatsAndHair: 'hair-and-hats',
    heads: 'all-heads-new-1',
    bodies: 'all-torsos-new-website',
    legs: 'all-legs',
    display: 'all-displays',
    accessories: 'all-accessories',
};

function buildSectionConfig(productSection) {
    return {
        id: productSection,
        title: productSection.charAt(0).toUpperCase() + productSection.slice(1),
        emptyText: `Choose a ${productSection}`,
        handle: SECTION_HANDLE_MAP[productSection] || productSection,
    };
}

const SearchBar = ({
    searchTerm,
    setSearchTerm,
    searchResults,
    setSearchResults,
    searchLoading,
    setSearchLoading,
    searchError,
    setSearchError,
    searchPageInfo,
    setSearchPageInfo,
    section,
    selected,
    currentBuildIndex,
    projectTitle,
    dispatch,
    selectProduct,
    selectNamedProduct,
    removeProduct,
    customiseProduct,
    skintone = '',
    onCloseSearch = null,
}) => {
    const [viewMode, setViewMode] = useState('search');
    const [activeTags, setActiveTags] = useState([]);
    const searchProductsScrollRef = useRef(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const searchBarRootRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isMobile && isCollapsed) {
            setIsCollapsed(false);
        }
    }, [isMobile, isCollapsed]);

    useEffect(() => {
        const builderInner = searchBarRootRef.current?.closest('.builder-inner');
        if (!builderInner) return;
        if (isMobile && isCollapsed) {
            builderInner.classList.add('search-collapsed');
        } else {
            builderInner.classList.remove('search-collapsed');
        }
        return () => {
            builderInner.classList.remove('search-collapsed');
        };
    }, [isCollapsed, isMobile]);

    useEffect(() => {
        const builderInner = searchBarRootRef.current?.closest('.builder-inner');
        if (!builderInner) return;
        if (isMobile && (viewMode === 'search' || viewMode === 'tags')) {
            builderInner.classList.add('search-mobile-hide-preview');
        } else {
            builderInner.classList.remove('search-mobile-hide-preview');
        }
        return () => builderInner.classList.remove('search-mobile-hide-preview');
    }, [isMobile, viewMode]);

    const loadMoreSearchResults = useCallback(
        async (afterCursorOverride) => {
            const endCursor = afterCursorOverride || searchPageInfo?.endCursor;
            if (!searchPageInfo?.hasNextPage || !endCursor) return;

            setSearchLoading(true);
            try {
                if (activeTags.length > 0) {
                    const result = await api.searchProductsByTags(activeTags, {
                        first: 50,
                        after: endCursor,
                        mode: 'AND',
                    });
                    setSearchResults([...(searchResults || []), ...result.products]);
                    setSearchPageInfo(result.pageInfo);
                } else {
                    const result = await api.searchProducts(searchTerm, 50, endCursor);
                    setSearchResults([...(searchResults || []), ...result.products]);
                    setSearchPageInfo(result.pageInfo);
                }
            } catch (err) {
                setSearchError('Error loading more results.');
            } finally {
                setSearchLoading(false);
            }
        },
        [
            activeTags,
            searchPageInfo,
            searchResults,
            searchTerm,
            setSearchLoading,
            setSearchError,
            setSearchResults,
            setSearchPageInfo,
        ]
    );

    const filteredSearchResults = useMemo(
        () =>
            (searchResults || []).filter((item) => {
                const node = item?.node || item;
                return isBuilderProduct(node);
            }),
        [searchResults]
    );

    useEffect(() => {
        if (!searchResults || searchResults.length === 0) return;
        let cancelled = false;
        (async () => {
            const enriched = await enrichProductsWithBoldOptions(searchResults, api);
            if (!cancelled) {
                const changed = enriched.some((p, i) => p !== searchResults[i]);
                if (changed) setSearchResults(enriched);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchResults, setSearchResults]);

    useEffect(() => {
        if (isMobile && filteredSearchResults && filteredSearchResults.length > 0) {
            setIsCollapsed(true);
        }
    }, [isMobile, filteredSearchResults]);

    const resetSearchState = useCallback(() => {
        setSearchResults(null);
        setSearchTerm('');
        setSearchError(null);
        setSearchPageInfo(null);
    }, [setSearchResults, setSearchTerm, setSearchError, setSearchPageInfo]);

    const performSearch = useCallback(async () => {
        if (searchLoading || !searchTerm.trim()) return;
        setActiveTags([]);
        setSearchLoading(true);
        setSearchError(null);
        try {
            const result = await api.searchProducts(searchTerm);
            setSearchResults(result.products);
            setSearchPageInfo(result.pageInfo);
            if (searchProductsScrollRef.current) {
                searchProductsScrollRef.current.scrollTop = 0;
            }
        } catch (err) {
            setSearchError('Error searching for products.');
        } finally {
            setSearchLoading(false);
        }
    }, [
        searchLoading,
        searchTerm,
        setActiveTags,
        setSearchLoading,
        setSearchError,
        setSearchResults,
        setSearchPageInfo,
    ]);

    const handleSelectProduct = useCallback(
        (product, attributes, addons, product_attribute, productFormJsonValues) => {
            const productSection = getProductSection(product);
            if (currentBuildIndex === undefined || currentBuildIndex === null || !product || !productSection) {
                console.error('Invalid product selection arguments', {
                    currentBuildIndex,
                    hasProduct: !!product,
                    productSection,
                });
                return;
            }
            try {
                const sectionConfig = buildSectionConfig(productSection);
                dispatch(selectSectionAndCategory(sectionConfig, null, skintone));
                dispatch(
                    selectProduct(
                        currentBuildIndex,
                        product,
                        attributes,
                        addons,
                        product_attribute,
                        productFormJsonValues
                    )
                );
            } catch (error) {
                console.error('Error selecting search product:', error);
            }
        },
        [currentBuildIndex, dispatch, selectProduct, skintone]
    );

    const handleSelectNamedProduct = useCallback(
        (currentBuildIndexArg, product, attributes, addons, product_attribute, productSection, productFormJsonValues) =>
            dispatch(
                selectNamedProduct(
                    currentBuildIndexArg,
                    product,
                    attributes,
                    addons,
                    product_attribute,
                    productSection,
                    productFormJsonValues
                )
            ),
        [dispatch, selectNamedProduct]
    );

    const handleRemoveProduct = useCallback(
        (sectionId) => dispatch(removeProduct(currentBuildIndex, sectionId)),
        [dispatch, currentBuildIndex, removeProduct]
    );

    const handleCustomiseProduct = useCallback((product) => customiseProduct(product), [customiseProduct]);

    return (
        <>
            <div
                ref={searchBarRootRef}
                className={`search-bar ${isMobile && isCollapsed ? 'search-bar--collapsed' : ''}`}
            >
                {isMobile && isCollapsed ? (
                    <div className="search-bar__collapsedHeader">
                        <button
                            type="button"
                            className="search-bar__backBtn"
                            onClick={() => setIsCollapsed(false)}
                            aria-label="Back to search"
                        >
                            ← Back
                        </button>
                        <div className="search-bar__resultsMeta">
                            {filteredSearchResults && filteredSearchResults.length > 0 && (
                                <span>
                                    {filteredSearchResults.length} result{filteredSearchResults.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="search-bar__controls">
                        <div className="search-bar__mode">
                            {/* Unified Back button logic:
                                1. If showing filtered search results -> Back clears results but stays in search mode
                                2. If in tag filter and tags active -> Back clears tags & results
                                3. If nothing selected/results empty -> Back exits global search (if onCloseSearch provided)
                            */}
                            <button
                                type="button"
                                className="search-bar__closeBtn"
                                onClick={() => {
                                    if (filteredSearchResults && filteredSearchResults.length > 0) {
                                        // Case 1: clear current results only
                                        resetSearchState();
                                    } else if (viewMode === 'tags' && activeTags.length > 0) {
                                        // Case 2: clear active tag(s)
                                        setActiveTags([]);
                                        resetSearchState();
                                        if (searchProductsScrollRef.current)
                                            searchProductsScrollRef.current.scrollTop = 0;
                                    } else if (viewMode === 'tags' && activeTags.length === 0) {
                                        // Go back to search mode from tags view if nothing selected
                                        setViewMode('search');
                                        resetSearchState();
                                    } else if (onCloseSearch) {
                                        // Case 3: exit search entirely
                                        resetSearchState();
                                        onCloseSearch();
                                    } else {
                                        // Fallback: just clear state
                                        resetSearchState();
                                    }
                                }}
                                aria-label="Back"
                            >
                                Back
                            </button>
                            {viewMode === 'search' ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setViewMode('tags');
                                        setSearchTerm('');
                                        setSearchResults(null);
                                        setSearchError(null);
                                        setSearchPageInfo(null);
                                    }}
                                    aria-pressed={viewMode === 'tags'}
                                    className={`search-bar__modeButton ${viewMode === 'tags' ? 'is-active' : ''}`}
                                >
                                    Filter
                                    <img
                                        src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-filter.svg?v=1758024533"
                                        alt=""
                                    />
                                </button>
                            ) : (
                                <button
                                    type="button btn span"
                                    onClick={() => {
                                        setViewMode('search');
                                        resetSearchState();
                                    }}
                                    aria-pressed={viewMode === 'search'}
                                    className={`search-bar__modeButton ${viewMode === 'search' ? 'is-active' : ''}`}
                                >
                                    Search
                                </button>
                            )}
                        </div>
                        {viewMode === 'search' && (
                            <>
                                <div className="search-bar__searchControls">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                performSearch();
                                            }
                                        }}
                                        placeholder="Search all parts..."
                                        className="search-bar__input"
                                        name="search_bar"
                                    />
                                </div>

                                <button
                                    onClick={performSearch}
                                    className="search-bar__submit button btn btn-pill"
                                    disabled={searchLoading || !searchTerm.trim()}
                                >
                                    <span>{searchLoading ? 'Searching...' : 'Search'}</span>
                                </button>
                            </>
                        )}
                        {viewMode === 'tags' && (
                            <TagFilter
                                activeTags={activeTags}
                                setActiveTags={setActiveTags}
                                setSearchLoading={setSearchLoading}
                                setSearchResults={setSearchResults}
                                setSearchPageInfo={setSearchPageInfo}
                                setSearchError={setSearchError}
                                resetSearchState={resetSearchState}
                                productsScrollRef={searchProductsScrollRef}
                                // Allow TagFilter to know unified Back exists so it doesn't render its own
                                suppressInternalClear
                            />
                        )}
                    </div>
                )}
            </div>
            {searchError && <div className="search-bar__error">{searchError}</div>}
            {filteredSearchResults && filteredSearchResults.length > 0 && (
                <Products
                    products={filteredSearchResults}
                    section={section}
                    selectedProducts={selected}
                    selected={selected[section]}
                    isSearchResults={true}
                    onSelect={handleSelectProduct}
                    onSelectNamedProduct={handleSelectNamedProduct}
                    currentBuildIndex={currentBuildIndex}
                    projectTitle={projectTitle}
                    onRemove={handleRemoveProduct}
                    onCustomise={handleCustomiseProduct}
                    skintone={skintone}
                    searchHasNextPage={searchPageInfo?.hasNextPage}
                    searchAfterCursor={searchPageInfo?.endCursor}
                    onLoadMoreSearch={loadMoreSearchResults}
                    searchLoading={searchLoading}
                    externalScrollRef={searchProductsScrollRef}
                />
            )}
            {searchResults && searchResults.length === 0 && (
                <div style={{ padding: '1rem 1rem 50vh', textAlign: 'center', color: '#666' }}>
                    No products found matching your search.
                </div>
            )}
            {filteredSearchResults &&
                filteredSearchResults.length === 0 &&
                searchResults &&
                searchResults.length > 0 && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                        No products found matching your search (excluding pre-made figures).
                    </div>
                )}
        </>
    );
};

export default SearchBar;

SearchBar.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    searchResults: PropTypes.array.isRequired,
    setSearchResults: PropTypes.func.isRequired,
    searchLoading: PropTypes.bool.isRequired,
    setSearchLoading: PropTypes.func.isRequired,
    searchError: PropTypes.string,
    setSearchError: PropTypes.func.isRequired,
    searchPageInfo: PropTypes.object,
    setSearchPageInfo: PropTypes.func.isRequired,
    section: PropTypes.string.isRequired,
    selected: PropTypes.object.isRequired,
    currentBuildIndex: PropTypes.number.isRequired,
    projectTitle: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    selectProduct: PropTypes.func.isRequired,
    selectNamedProduct: PropTypes.func.isRequired,
    removeProduct: PropTypes.func.isRequired,
    customiseProduct: PropTypes.func.isRequired,
    skintone: PropTypes.string,
    onCloseSearch: PropTypes.func,
};
